import { useState } from 'react';
import { usePermission } from '@/hooks/usePermission';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, Tab, Button, Spinner, Switch, Input } from '@heroui/react';
import { PageHeader } from '@/components/common/PageHeader';
import { CopyableField } from '@/components/common/CopyableField';
import { StatusChip } from '@/components/common/StatusChip';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DomainStatusChip } from '@/features/domains/components/DomainStatusChip';
import { ManagedDnsPanel } from '@/features/domains/components/ManagedDnsPanel';
import { OnboardingChecklist } from '@/features/domains/components/OnboardingChecklist';
import { CatchAllSetting } from '@/features/domains/components/CatchAllSetting';
import {
  useDomain, useDomainDns, useVerifyDomain,
  useRotateDkim, useSetDomainMailState, useDeleteDomain,
  useDnsZones, useCreateDnsZone,
} from '@/features/domains/hooks';
import { notify } from '@/lib/notify';
import { ShieldCheckIcon, ArrowPathIcon, KeyIcon, PauseCircleIcon, PlayCircleIcon, TrashIcon } from '@/icons';

/** Domain detail: DNS management (managed zone editing or external publish), verification and settings. */
export function DomainDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: domain, isLoading, isError } = useDomain(id);
  const { data: records, isLoading: dnsLoading } = useDomainDns(id);
  const verify = useVerifyDomain(id);
  const zones = useDnsZones();
  const createZone = useCreateDnsZone();
  const rotateDkim = useRotateDkim(id);
  const mailState = useSetDomainMailState(id);
  const del = useDeleteDomain();
  const canWrite = usePermission().can('zone:write');

  const [confirmDkim, setConfirmDkim] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');

  if (isLoading) return <div className="flex justify-center py-20"><Spinner label="Loading domain…" /></div>;
  if (isError || !domain) {
    return <div className="py-20 text-center"><p className="text-fg-muted">Domain not found.</p>
      <Button className="mt-4" variant="flat" onPress={() => navigate('/domains')}>Back to domains</Button></div>;
  }

  // A managed DNS zone is the source of truth. The domain API's legacy
  // dnsMode field is not persisted and must not be used to infer zone state.
  const managedZone = zones.data?.find((zone) => zone.mailDomainId === domain.id);
  const managed = Boolean(managedZone);
  const suspended = domain.status === 'suspended';
  const dnsRecords = records ?? [];
  const ownershipRecord = dnsRecords.find((record) => record.purpose.toLowerCase() === 'verification');
  const mailRecords = dnsRecords.filter((record) => record.purpose.toLowerCase() !== 'verification' && record.required !== false);
  const mailReady = mailRecords.length > 0 && mailRecords.every((record) => (record.status ?? (record.verified ? 'ok' : 'pending')) === 'ok');
  const lastCheckedAt = dnsRecords
    .map((record) => record.checkedAt ? Date.parse(record.checkedAt) : Number.NaN)
    .filter(Number.isFinite)
    .sort((a, b) => b - a)[0];

  return (
    <div>
      <PageHeader
        title={domain.domain}
        actions={
          <Button color="primary" startContent={<ShieldCheckIcon className="h-4 w-4" />} isLoading={verify.isPending} onPress={() => verify.mutate()}>
            Verify now
          </Button>
        }
      />
      <div className="mb-4 flex items-center gap-3">
        <DomainStatusChip domain={domain} />
        <StatusChip tone={managed ? 'info' : 'neutral'}>{managed ? 'Managed DNS' : 'External DNS'}</StatusChip>
        {domain.verifiedAt && <span className="text-xs text-fg-subtle">Verified {new Date(domain.verifiedAt).toLocaleDateString()}</span>}
      </div>

      <OnboardingChecklist domainId={domain.id} />

      <Tabs aria-label="Domain details" variant="underlined" color="primary">
        <Tab key="dns" title="DNS records">
          {managed ? (
            <ManagedDnsPanel domain={domain} />
          ) : (
            <div className="rounded-xl border border-border bg-surface p-5">
              <p className="mb-4 text-sm text-fg-muted">Publish these records at your DNS provider, then click Verify. Switch to managed DNS in Settings to let Nifty host the zone.</p>
              <div className="mb-4 rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm text-fg-muted">
                <b className="text-fg">External DNS:</b> the Host / Name values below are relative to <b>{domain.domain}</b> and are safe to paste into providers such as Afrihost. Use <b>@</b> for the root domain. Do not append the domain again.
              </div>
              {dnsLoading ? <div className="flex justify-center py-8"><Spinner /></div> : (
                <div className="space-y-4">
                  {(records ?? []).map((r, i) => {
                    const hostLabel = relativeDnsHost(r.host, domain.domain);
                    const status = r.status ?? (r.verified ? 'ok' : 'pending');
                    return (
                    <div key={`${r.type}-${i}`} className="rounded-lg border border-border p-4">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-sm font-semibold text-fg">{r.purpose} <span className="text-fg-subtle">({r.type})</span></span>
                        {status === 'ok' && <StatusChip tone="success">Found</StatusChip>}
                        {status === 'mismatch' && <StatusChip tone="danger">Conflict</StatusChip>}
                        {status === 'missing' && <StatusChip tone="warning">Missing</StatusChip>}
                        {status === 'pending' && <StatusChip tone="warning">Pending</StatusChip>}
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <CopyableField label="Host / Name" value={hostLabel} />
                        <CopyableField label={r.priority != null ? `Value (priority ${r.priority})` : 'Value'} value={r.value} />
                      </div>
                      <p className="mt-2 text-xs text-fg-subtle">Full DNS name: {r.host}</p>
                      {status === 'mismatch' && r.observedValue && (
                        <div className="mt-3 rounded-md border border-danger/20 bg-danger/5 p-2 text-xs text-danger">
                          Conflicting value found: <span className="break-all font-mono">{r.observedValue}</span>
                        </div>
                      )}
                    </div>
                  );})}
                </div>
              )}
            </div>
          )}
        </Tab>

        <Tab key="verification" title="Verification">
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <VerificationState
                title="Domain ownership"
                ready={domain.verified}
                detail={domain.verified
                  ? 'The ownership record is verified and will not be requested again.'
                  : ownershipRecord?.status === 'mismatch'
                    ? 'The ownership record has a conflicting value.'
                    : 'Publish the verification TXT record before the first verification.'}
              />
              <VerificationState
                title="Email DNS readiness"
                ready={mailReady}
                detail={mailReady
                  ? 'All required MX, SPF, DKIM, DMARC and MTA-STS records were found.'
                  : `${mailRecords.filter((record) => (record.status ?? (record.verified ? 'ok' : 'pending')) !== 'ok').length} required record(s) still need attention.`}
              />
            </div>
            <div className="rounded-xl border border-border bg-surface p-6">
              <div className="flex items-start gap-4">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-surface-raised text-fg-muted"><ArrowPathIcon className="h-5 w-5" /></span>
                <div>
                  <p className="text-sm font-medium text-fg">Refresh public DNS results</p>
                  <p className="mt-1 text-sm text-fg-muted">
                    Re-checking an established domain only refreshes the record results. It does not replace DKIM keys or reset the original verification date.
                  </p>
                  <p className="mt-2 text-xs text-fg-subtle">
                    {lastCheckedAt ? `Last checked ${new Date(lastCheckedAt).toLocaleString()}` : 'Not checked yet'}
                  </p>
                  <Button className="mt-4" size="sm" variant="flat" isLoading={verify.isPending} onPress={() => verify.mutate()}>Re-check now</Button>
                </div>
              </div>
            </div>
          </div>
        </Tab>

        <Tab key="settings" title="Settings">
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-surface p-5">
              <SettingRow title="Catch-all" desc={domain.verified
                ? 'Send mail for addresses that do not exist to one of your mailboxes. Off by default: a catch-all also receives spam.'
                : 'Available once the domain is verified. Mail for addresses that do not exist is then sent to a mailbox you choose.'}>
                <CatchAllSetting domain={domain} canWrite={canWrite} />
              </SettingRow>
              <SettingRow title="Managed DNS" desc="Let Nifty host the DNS zone so you can edit records here instead of at an external provider.">
                <Switch
                  isSelected={managed}
                  isDisabled={zones.isLoading || zones.isError || createZone.isPending || managed}
                  onValueChange={(enabled) => {
                    if (enabled && !managed) createZone.mutate(domain.id);
                  }}
                  aria-label="Enable managed DNS"
                />
              </SettingRow>
              <SettingRow title="DKIM key" desc="Rotate the DKIM signing key. The previous key stays valid during an overlap window so mail keeps authenticating.">
                <Button size="sm" variant="flat" startContent={<KeyIcon className="h-4 w-4" />} isLoading={rotateDkim.isPending} onPress={() => setConfirmDkim(true)}>Rotate</Button>
              </SettingRow>
              <SettingRow title="Mail flow" desc={suspended ? 'Mail is suspended for this domain — inbound is rejected and outbound is held.' : 'Mail is flowing normally for this domain.'}>
                {suspended
                  ? <Button size="sm" variant="flat" color="success" startContent={<PlayCircleIcon className="h-4 w-4" />} isLoading={mailState.isPending} onPress={() => mailState.mutate('resume')}>Resume mail</Button>
                  : <Button size="sm" variant="flat" color="warning" startContent={<PauseCircleIcon className="h-4 w-4" />} isLoading={mailState.isPending} onPress={() => mailState.mutate('suspend')}>Suspend mail</Button>}
              </SettingRow>
            </div>

            {canWrite && <div className="rounded-xl border border-danger/30 bg-danger/5 p-5">
              <SettingRow title="Delete domain" desc="Permanently removes the domain and all its mail objects. Remove mailboxes first.">
                <Button size="sm" variant="flat" color="danger" startContent={<TrashIcon className="h-4 w-4" />} onPress={() => { setDeleteText(''); setConfirmDelete(true); }}>Delete</Button>
              </SettingRow>
            </div>}
          </div>
        </Tab>
      </Tabs>

      <ConfirmDialog
        isOpen={confirmDkim} onClose={() => setConfirmDkim(false)}
        onConfirm={() => { rotateDkim.mutate(undefined, { onSuccess: () => setConfirmDkim(false) }); }}
        title="Rotate DKIM key?"
        body={<>A new signing key is generated immediately. In managed mode the public key is published for you; in external mode you must publish the new TXT record before the overlap window ends.</>}
        confirmLabel="Rotate key" tone="primary" isLoading={rotateDkim.isPending}
      />

      <ConfirmDialog
        isOpen={confirmDelete} onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          if (deleteText.trim() !== domain.domain) { notify.error('Type the domain name exactly to confirm.'); return; }
          del.mutate(id, { onSuccess: () => { setConfirmDelete(false); navigate('/domains'); } });
        }}
        title="Delete this domain?"
        body={
          <div className="space-y-3">
            <p>This permanently deletes <b>{domain.domain}</b> and every mailbox, alias and forwarder under it. This cannot be undone.</p>
            <Input aria-label="Type the domain to confirm" variant="bordered" placeholder={domain.domain}
              value={deleteText} onValueChange={setDeleteText} />
          </div>
        }
        confirmLabel="Delete domain" tone="danger" isLoading={del.isPending}
      />
    </div>
  );
}

function VerificationState({ title, ready, detail }: { title: string; ready: boolean; detail: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface p-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-fg">{title}</p>
        <StatusChip tone={ready ? 'success' : 'warning'}>{ready ? 'Ready' : 'Needs attention'}</StatusChip>
      </div>
      <p className="mt-2 text-sm text-fg-muted">{detail}</p>
    </div>
  );
}

function relativeDnsHost(host: string, domain: string): string {
  const normalizedHost = host.replace(/\.$/, '').toLowerCase();
  const normalizedDomain = domain.replace(/\.$/, '').toLowerCase();
  if (normalizedHost === normalizedDomain) return '@';
  const suffix = `.${normalizedDomain}`;
  return normalizedHost.endsWith(suffix) ? host.slice(0, -suffix.length) : host;
}

function SettingRow({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border py-3 last:border-b-0">
      <div>
        <p className="text-sm font-medium text-fg">{title}</p>
        <p className="text-xs text-fg-muted">{desc}</p>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}
